import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { ReportDef } from '@/components/reports/reportDefs'
import {
  contarActivos,
  estadoMulti,
  filterStateKeys,
  valoresMulti,
  type ReportFiltersState,
} from '@/components/reports/filterPredicates'
import { CAMPOS_VOCABULARIO, normalizarValor } from '@/components/reports/vocabulario'
import { useFiltrosPersistentes } from '@/hooks/useFiltrosPersistentes'

/**
 * Estado de filtros y nivel de un report, sincronizado con la query string para
 * que un report filtrado se pueda enlazar y sobreviva a un refresco.
 *
 * Sigue el patrón de useProduccionDiariaFilters: cada setter escribe en la URL
 * con `replace: true` para no llenar el historial de pasos intermedios mientras
 * el usuario teclea.
 */
export function useReportFilters(def: ReportDef) {
  const [params, setParams] = useSearchParams()

  // Los filtros aguantan irse a otra pantalla y volver, durante la sesión. Un
  // ámbito POR REPORT: cada pestaña es su propia pantalla, con sus propios
  // filtros, y compartirlos daría una tabla filtrada por un campo que ni existe.
  // Aquí no hay que leer `paramsIniciales`: este hook no guarda estado propio,
  // deriva de la URL en cada render, así que con reponer la URL basta.
  const { olvidar } = useFiltrosPersistentes(`reports.${def.key}`)

  const filters: ReportFiltersState = useMemo(() => {
    const out: ReportFiltersState = {}
    for (const spec of def.filters) {
      for (const k of filterStateKeys(spec)) {
        const v = params.get(k)
        if (!v) continue
        // Un enlace guardado puede traer el vocabulario viejo de las vistas
        // (`?seccion=TERMINADO`), y sin traducirlo el filtro no casaría con nada:
        // el usuario vería «0 productos» con un chip que parece correcto, sin
        // error por ningún lado. Sólo se traducen los campos con vocabulario
        // propio; los de código —artículo, pedido, lote— no se tocan.
        const conVocabulario = spec.field
          && (CAMPOS_VOCABULARIO as readonly string[]).includes(spec.field)
        out[k] = conVocabulario
          ? estadoMulti(valoresMulti(v).map(x => normalizarValor(spec.field as string, x)))
          : v
      }
    }
    return out
  }, [params, def])

  /**
   * Niveles sobre los que se calculan los totales, en el orden del report.
   *
   * VARIOS a la vez, cada uno con su propia fila de pie. Antes era de opción
   * única con este argumento: «la cabecera ya es la suma de su detalle, así que
   * totalizar los dos a la vez duplicaría». Y es cierto — pero sólo si se suman
   * EN LA MISMA CELDA. En filas separadas no se duplica nada: cada fila totaliza
   * su nivel, y poder ver a la vez los litros de los lotes y los kilos de los
   * componentes es justo lo que se quería del selector.
   *
   * Se guardan en la URL separados por la barra vertical, igual que los filtros
   * multi-valor. El nivel de cabecera a solas es el valor por defecto y no se
   * escribe, para que la ruta limpia siga siendo la del estado por defecto.
   */
  const niveles = useMemo(() => [def.headerType, ...def.detailTypes], [def])

  const aggLevels = useMemo(() => {
    // Mismo motivo que en los filtros: un `?nivel=COMPONENTE` guardado se valida
    // contra la lista de niveles y, sin traducir, caería al de cabecera sin avisar.
    const pedidos = valoresMulti(params.get('nivel') ?? '')
      .map(n => normalizarValor('tipoFila', n))
    // Se ordenan por la jerarquía y no por el orden en que se eligieron: el pie
    // tiene que leerse siempre de arriba abajo igual.
    const validos = niveles.filter(n => pedidos.includes(n))
    return validos.length > 0 ? validos : [def.headerType]
  }, [params, niveles, def])

  /** ¿Hay algo distinto del valor por defecto? Lo usa el resalte del control. */
  const activeLevel = aggLevels.length !== 1 || aggLevels[0] !== def.headerType
    ? estadoMulti(aggLevels)
    : null

  const setFilter = useCallback((key: string, value: string) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) next.set(key, value)
      else next.delete(key)
      next.delete('page')
      return next
    }, { replace: true })
  }, [setParams])

  /**
   * ¿El pie cuenta sólo las sublíneas que casan con el filtro?
   *
   * Por defecto SÍ, que es lo que espera quien busca un componente: el total de
   * lo que ha pedido. El modo contrario —el bloque entero— sigue disponible
   * porque a veces la pregunta es «de los productos que usan este componente,
   * cuánto consumen en total», y entonces el bloque completo es la respuesta.
   */
  const soloCoincidencias = params.get('total') !== 'bloque'

  const setSoloCoincidencias = useCallback((solo: boolean) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      // El valor por defecto no se escribe: la ruta limpia es la del defecto.
      if (solo) next.delete('total')
      else next.set('total', 'bloque')
      next.delete('page')
      return next
    }, { replace: true })
  }, [setParams])

  const setLevels = useCallback((tipoFilas: string[]) => {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      const limpios = niveles.filter(n => tipoFilas.includes(n))
      // Sin ninguno elegido se vuelve al valor por defecto en vez de dejar el pie
      // sin totales: un pie vacío parece que la pantalla se ha roto.
      const esDefecto = limpios.length === 0
        || (limpios.length === 1 && limpios[0] === def.headerType)
      if (esDefecto) next.delete('nivel')
      else next.set('nivel', estadoMulti(limpios))
      next.delete('page')
      return next
    }, { replace: true })
  }, [setParams, def, niveles])

  const clearFilters = useCallback(() => {
    // La URL resultante ya borra o reescribe el recuerdo de la sesión —lo
    // guardado es siempre lo que dice la URL—; `olvidar` lo hace explícito.
    olvidar()
    setParams(prev => {
      const next = new URLSearchParams(prev)
      for (const spec of def.filters) {
        for (const k of filterStateKeys(spec)) next.delete(k)
      }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setParams, def, olvidar])

  return {
    filters,
    setFilter,
    clearFilters,
    activeCount: contarActivos(def, filters),
    activeLevel,
    aggLevels,
    setLevels,
    soloCoincidencias,
    setSoloCoincidencias,
  }
}
